import { omit } from "lodash";
import {
  AssetLinkingInput,
  GetAssetLinkingDetailsQuery,
  useAddAssetLinkingMutation,
  useEditAssetLinkingMutation,
  useGetAssetLinkingDetailsQuery,
} from "../../../graphql/generated";
import AssetLinkingFormModal, {
  AssetLinkingFormModalProps,
} from "./AssetLinkingFormModal";
import LoadingOverlay from "../../common/LoadingOverlay";
import { AssetConflict } from "../../trip-planning/PlanningTool/assetValidation";

type AssetLinkingFormModalContainerProps = Omit<
  AssetLinkingFormModalProps,
  "assetLinking" | "onSubmit" | "error"
> & {
  assetLinkingId?: string;
  initialAssetLinking?: AssetLinkingDetailsData;
  onSaved?: (assetLinkingId: string) => void;
  overrideCurrentAssignment?: boolean;
  deferSave?: boolean;
  onAssetLinkingChange?: (
    driverId: string,
    assetLinking: AssetLinkingInput,
    assetLinkingId?: string
  ) => void;
  validateAssetAssignment?: (
    assetLinkingInput: AssetLinkingInput,
    currentDriverId: string
  ) => AssetConflict[];
};

type AssetLinkingDetailsData = Exclude<
  GetAssetLinkingDetailsQuery["assetLinking"],
  null | undefined
>;

/**
 * Formats asset conflicts into a user-friendly error message
 */
const formatConflictError = (conflicts: AssetConflict[]): string => {
  if (conflicts.length === 0) {
    return "";
  }

  const formatPeriod = (start: Date | null, end: Date | null): string => {
    // If both start and end are null, it's a permanent assignment
    if (!start && !end) {
      return "permanently assigned";
    }
    
    // If only start is null, assignment is valid until end date
    if (!start && end) {
      return `until ${end.toLocaleDateString()}`;
    }
    
    // If only end is null, assignment starts from start date with no end
    if (start && !end) {
      return `from ${start.toLocaleDateString()} onwards`;
    }
    
    // Both dates are present (TypeScript needs assertion since it can't infer from control flow)
    return `from ${start!.toLocaleDateString()} to ${end!.toLocaleDateString()}`;
  };

  const conflictMessages = conflicts.map((conflict) => {
    const assetTypeLabel =
      conflict.assetType === "tractor"
        ? "Tractor"
        : conflict.assetType === "trailer"
        ? "Trailer"
        : "Additional Trailer";
    
    const period = formatPeriod(conflict.conflictingPeriod.start, conflict.conflictingPeriod.end);
    
    return `${assetTypeLabel} "${conflict.assetName}" is already assigned to driver "${conflict.conflictingDriverName}" (${period})`;
  });

  return `Asset assignment conflict:\n${conflictMessages.join("\n")}`;
};

const assetLinkingToInput = (
  assetLinking: AssetLinkingDetailsData | null
): AssetLinkingInput | null => {
  if (!assetLinking) {
    return null;
  }
  return {
    ...omit(assetLinking, "_id"),
    driver: assetLinking.driver?._id || null,
    trailer: assetLinking.trailer?._id || null,
    tractor: assetLinking.tractor?._id || null,
    additionalTrailers: assetLinking.additionalTrailers?.map(
      (trailer) => trailer._id
    ),
  };
};

const AssetLinkingFormModalContainer = (
  props: AssetLinkingFormModalContainerProps
) => {
  const assetLinkingQuery = useGetAssetLinkingDetailsQuery(
    {
      id: props.assetLinkingId || "",
    },
    {
      enabled: !!props.assetLinkingId && !props.initialAssetLinking,
    }
  );

  const addAssetLinkingMutation = useAddAssetLinkingMutation();
  const editAssetLinkingMutation = useEditAssetLinkingMutation();

  const existingAssetLinking =
    props.initialAssetLinking || assetLinkingQuery?.data?.assetLinking || null;

  const submitAssetLinking = async (assetLinking: AssetLinkingInput) => {
    // Validate asset assignment if validation function is provided
    if (props.validateAssetAssignment && assetLinking.driver) {
      const conflicts = props.validateAssetAssignment(
        assetLinking,
        assetLinking.driver
      );
      
      if (conflicts.length > 0) {
        // Return validation error - will be handled by the modal
        throw new Error(formatConflictError(conflicts));
      }
    }

    // If deferSave is true, call the callback instead of saving
    if (props.deferSave && props.onAssetLinkingChange) {
      const driverId = assetLinking.driver;
      if (driverId) {
        props.onAssetLinkingChange(
          driverId,
          assetLinking,
          existingAssetLinking?._id
        );
      }
      props.onClose();
      return;
    }

    // Default behavior: immediate save
    const existingAssetLinkingIsPermanent = !existingAssetLinking?.endDate;
    if (
      props.overrideCurrentAssignment &&
      existingAssetLinking &&
      existingAssetLinkingIsPermanent
    ) {
      const response = await addAssetLinkingMutation.mutateAsync({
        newAssetLinkingData: assetLinking,
      });
      props.onSaved?.(response.addAssetLinking._id);
    } else if (existingAssetLinking?._id) {
      const response = await editAssetLinkingMutation.mutateAsync({
        id: existingAssetLinking._id,
        editAssetLinkingData: assetLinking,
      });
      props.onSaved?.(response.editAssetLinking._id);
    } else {
      const response = await addAssetLinkingMutation.mutateAsync({
        newAssetLinkingData: assetLinking,
      });
      props.onSaved?.(response.addAssetLinking._id);
    }
    if (props.assetLinkingId) {
      assetLinkingQuery.refetch();
    }
    props.onClose();
  };

  if (
    props.assetLinkingId &&
    !props.initialAssetLinking &&
    assetLinkingQuery.isLoading
  ) {
    return <LoadingOverlay loading />;
  }

  return (
    <AssetLinkingFormModal
      assetLinking={assetLinkingToInput(existingAssetLinking)}
      onSubmit={submitAssetLinking}
      loading={
        addAssetLinkingMutation.isLoading ||
        editAssetLinkingMutation.isLoading ||
        (!!props.assetLinkingId && assetLinkingQuery?.isLoading)
      }
      error={
        addAssetLinkingMutation.error ||
        editAssetLinkingMutation.error ||
        assetLinkingQuery.error
      }
      {...props}
    />
  );
};

export default AssetLinkingFormModalContainer;
